import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LegacyUserForMigration } from '../../models/legacy-user.model';
import { parseUnitHierarchy, filterOptions, sortByIdInParentheses } from './user-migration.utils';
import { FILTER_DEFAULT_VALUE, FILTER_EXCLUSIONS } from './user-migration.constants';

export interface FilterState {
  searchText: string;
  selectedLibraryLevel: string | null;
  selectedArea: string | null;
  selectedDistrito: string | null;
  selectedComunidad: string | null;
}

export interface FilterOptions {
  libraryLevels: string[];
  areas: string[];
  distritos: string[];
  filteredDistritos: string[];
  comunidades: string[];
  filteredComunidades: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserMigrationFilterService {
  private readonly filterState$ = new BehaviorSubject<FilterState>({
    searchText: '',
    selectedLibraryLevel: null,
    selectedArea: null,
    selectedDistrito: null,
    selectedComunidad: null
  });

  private readonly filterOptions$ = new BehaviorSubject<FilterOptions>({
    libraryLevels: [],
    areas: [],
    distritos: [],
    filteredDistritos: [],
    comunidades: [],
    filteredComunidades: []
  });

  // Store all users for filtering
  private allUsers: LegacyUserForMigration[] = [];

  constructor() {}

  /**
   * Get current filter state
   */
  getCurrentState(): FilterState {
    return this.filterState$.value;
  }

  /**
   * Get current filter options
   */
  getCurrentOptions(): FilterOptions {
    return this.filterOptions$.value;
  }

  /**
   * Update all users for filtering operations
   */
  setAllUsers(users: LegacyUserForMigration[]): void {
    this.allUsers = users;
  }

  /**
   * Update library levels in filter options
   */
  setLibraryLevels(levels: string[]): void {
    const options = this.filterOptions$.value;
    options.libraryLevels = levels.sort((a, b) => {
      const idA = parseInt(a.split(' - ')[0]);
      const idB = parseInt(b.split(' - ')[0]);
      return isNaN(idA) || isNaN(idB) ? a.localeCompare(b) : idA - idB;
    });
    this.filterOptions$.next({ ...options });
  }

  /**
   * Update hierarchical units (areas, distritos, comunidades)
   */
  setHierarchicalUnits(areas: string[], distritos: string[], comunidades: string[]): void {
    const options = this.filterOptions$.value;
    options.areas = areas.sort();
    options.distritos = distritos.sort((a, b) => sortByIdInParentheses(a, b));
    options.filteredDistritos = [...options.distritos];
    options.comunidades = comunidades.sort();
    options.filteredComunidades = [...options.comunidades];
    this.filterOptions$.next({ ...options });
  }

  /**
   * Update search text
   */
  setSearchText(searchText: string): void {
    const state = this.filterState$.value;
    state.searchText = searchText;
    this.filterState$.next({ ...state });
  }

  /**
   * Update library level filter
   */
  setLibraryLevel(level: string | null): void {
    const state = this.filterState$.value;
    state.selectedLibraryLevel = this.normalizeFilterValue(level);
    this.filterState$.next({ ...state });
  }

  /**
   * Update area filter and cascade changes
   */
  setArea(area: string | null): void {
    const state = this.filterState$.value;
    const options = this.filterOptions$.value;

    state.selectedArea = this.normalizeFilterValue(area);
    state.selectedDistrito = null;
    state.selectedComunidad = null;

    // Always show all options - filtering happens on table data, not dropdowns
    options.filteredDistritos = [...options.distritos];
    options.filteredComunidades = [...options.comunidades];

    this.filterState$.next({ ...state });
    this.filterOptions$.next({ ...options });
  }

  /**
   * Update distrito filter and cascade changes
   */
  setDistrito(distrito: string | null): void {
    const state = this.filterState$.value;
    const options = this.filterOptions$.value;

    state.selectedDistrito = this.normalizeFilterValue(distrito);

    this.filterState$.next({ ...state });
    this.filterOptions$.next({ ...options });
  }

  /**
   * Update comunidad filter
   */
  setComunidad(comunidad: string | null): void {
    const state = this.filterState$.value;
    state.selectedComunidad = this.normalizeFilterValue(comunidad);
    this.filterState$.next({ ...state });
  }

  /**
   * Get filtered library levels for autocomplete
   */
  getFilteredLevels(): string[] {
    return this.filterOptions$.value.libraryLevels;
  }

  /**
   * Get filtered areas for autocomplete
   */
  getFilteredAreas(): string[] {
    return this.filterOptions$.value.areas;
  }

  /**
   * Get filtered distritos for autocomplete
   */
  getFilteredDistritos(): string[] {
    return this.filterOptions$.value.filteredDistritos;
  }

  /**
   * Get filtered comunidades for autocomplete
   */
  getFilteredComunidades(): string[] {
    return this.filterOptions$.value.filteredComunidades;
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    const options = this.filterOptions$.value;
    options.filteredDistritos = [...options.distritos];
    options.filteredComunidades = [...options.comunidades];

    this.filterState$.next({
      searchText: '',
      selectedLibraryLevel: null,
      selectedArea: null,
      selectedDistrito: null,
      selectedComunidad: null
    });

    this.filterOptions$.next({ ...options });
  }

  /**
   * Private helper: normalize filter value
   */
  private normalizeFilterValue(value: string | null): string | null {
    if (!value || FILTER_EXCLUSIONS.includes(value)) {
      return null;
    }
    return value === FILTER_DEFAULT_VALUE ? null : value;
  }

}

